import * as React from 'react';

import { ZonedDateTime } from '@internationalized/date';
import { ContentCard, LocalizedDate, cn } from '@rematter/ui-web';
import {
  Anchor,
  CheckCircle,
  Package,
  Ship,
  Truck,
  Warehouse,
  XCircle,
} from 'lucide-react';

import { useTranslations } from '../../i18n/useTranslations';
import {
  ContainerRecord,
  ContainerStatus,
  enumContainerStatus,
} from '../../types/container';

interface ContainerTabTimelineProps {
  container: ContainerRecord;
}

type TimelineStep = {
  status: ContainerStatus;
  label: string;
  icon: React.ReactNode;
  timestamp: ZonedDateTime | null;
  description: string;
};

const STATUS_ORDER: ContainerStatus[] = [
  enumContainerStatus.AtPort,
  enumContainerStatus.InTransitToFacility,
  enumContainerStatus.AtFacilityEmpty,
  enumContainerStatus.AtFacilityLoaded,
  enumContainerStatus.InTransitToPort,
  enumContainerStatus.AtPortReadyToShip,
  enumContainerStatus.Shipped,
];

const getStatusIndex = (status: ContainerStatus): number => {
  if (status === enumContainerStatus.Voided) return -1;
  return STATUS_ORDER.indexOf(status);
};

export const ContainerTabTimeline: React.FC<ContainerTabTimelineProps> = ({
  container,
}) => {
  const t = useTranslations();

  const timelineSteps: TimelineStep[] = React.useMemo(
    () => [
      {
        status: enumContainerStatus.AtPort,
        label: t.containersView.statuses.atPort,
        icon: <Anchor className="size-4" />,
        timestamp: container.timestamps.createdAt ?? null,
        description: t.containersView.timeline.descriptions.atPort,
      },
      {
        status: enumContainerStatus.InTransitToFacility,
        label: t.containersView.statuses.inTransitToFacility,
        icon: <Truck className="size-4" />,
        timestamp: container.pickedUpAt ?? null,
        description: t.containersView.timeline.descriptions.inTransitToFacility,
      },
      {
        status: enumContainerStatus.AtFacilityEmpty,
        label: t.containersView.statuses.atFacilityEmpty,
        icon: <Warehouse className="size-4" />,
        timestamp: container.arrivedAtFacilityAt ?? null,
        description: t.containersView.timeline.descriptions.atFacilityEmpty,
      },
      {
        status: enumContainerStatus.AtFacilityLoaded,
        label: t.containersView.statuses.atFacilityLoaded,
        icon: <Package className="size-4" />,
        timestamp: container.loadedAt ?? null,
        description: t.containersView.timeline.descriptions.atFacilityLoaded,
      },
      {
        status: enumContainerStatus.InTransitToPort,
        label: t.containersView.statuses.inTransitToPort,
        icon: <Truck className="size-4" />,
        timestamp: container.departedFacilityAt ?? null,
        description: t.containersView.timeline.descriptions.inTransitToPort,
      },
      {
        status: enumContainerStatus.AtPortReadyToShip,
        label: t.containersView.statuses.atPortReadyToShip,
        icon: <Anchor className="size-4" />,
        timestamp: container.returnedToPortAt ?? null,
        description: t.containersView.timeline.descriptions.atPortReadyToShip,
      },
      {
        status: enumContainerStatus.Shipped,
        label: t.containersView.statuses.shipped,
        icon: <Ship className="size-4" />,
        timestamp: container.shippedAt ?? null,
        description: t.containersView.timeline.descriptions.shipped,
      },
    ],
    [container, t],
  );

  const currentStatusIndex = getStatusIndex(container.status);
  const isVoided = container.status === enumContainerStatus.Voided;

  return (
    <div className="@ContainerTabTimeline flex max-h-full flex-col gap-5 p-5">
      <ContentCard>
        {isVoided && (
          <div className="mb-6 flex items-center gap-2 rounded-lg bg-failure/10 p-4 text-failure">
            <XCircle className="size-5" />
            <span className="font-medium">
              {t.containersView.timeline.voided}
            </span>
          </div>
        )}

        <div className="relative">
          {timelineSteps.map((step, index) => {
            const stepIndex = getStatusIndex(step.status);
            const isCompleted = !isVoided && stepIndex < currentStatusIndex;
            const isCurrent = !isVoided && stepIndex === currentStatusIndex;
            const isPending = isVoided || stepIndex > currentStatusIndex;

            return (
              <div
                key={step.status}
                className="relative flex gap-4 pb-8 last:pb-0"
              >
                {/* Vertical line */}
                {index < timelineSteps.length - 1 && (
                  <div
                    className={cn(
                      'absolute top-8 left-4 h-full w-0.5 -translate-x-1/2',
                      isCompleted ? 'bg-success' : 'bg-neutral/20',
                    )}
                  />
                )}

                {/* Icon circle */}
                <div
                  className={cn(
                    'relative z-10 flex size-8 shrink-0 items-center justify-center rounded-full border-2',
                    isCompleted && 'border-success bg-success text-white',
                    isCurrent && 'border-primary bg-primary text-white',
                    isPending && 'border-neutral/30 bg-white text-neutral/40',
                  )}
                >
                  {isCompleted ? <CheckCircle className="size-4" /> : step.icon}
                </div>

                {/* Content */}
                <div className="flex-1 pt-0.5">
                  <div className="flex items-center gap-2">
                    <h4
                      className={cn(
                        'font-medium',
                        isPending && 'text-neutral/40',
                      )}
                    >
                      {step.label}
                    </h4>
                    {isCurrent && (
                      <span className="rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                        {t.containersView.timeline.current}
                      </span>
                    )}
                  </div>
                  <p
                    className={cn(
                      'mt-0.5 text-sm',
                      isPending ? 'text-neutral/30' : 'text-neutral/60',
                    )}
                  >
                    {step.description}
                  </p>
                  {step.timestamp && !isPending && (
                    <div className="mt-1 text-xs text-neutral/40">
                      <LocalizedDate date={step.timestamp} dateStyle="medium" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </ContentCard>
    </div>
  );
};
